// Recent working postcodes for the current engineer (DEV-51044). Shared by
// the postcode screen and the Change Location drawer so both offer the same
// chips and both see a write straight away.

import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { useParams } from 'react-router-dom'

import { useMe } from '../auth/hooks/useMe'
import { normalizePostcode } from '../services/postcodes'
import {
  buildRecentLocationsKey,
  MAX_RECENT_LOCATIONS,
  parseRecentLocations,
  readRecentLocationsRaw,
  rememberRecentLocation,
  subscribeToRecentLocations,
} from '../services/recentLocations'

export interface UseRecentLocationsResult {
  /** Distinct postcodes, newest first, excluding the one already in use. */
  recentPostcodes: string[]
  /** Record a postcode as the most recent used on this device. */
  rememberLocation: (postcode: string) => void
}

/**
 * @param currentPostcode The postcode in use, filtered out of the chips —
 *   tapping it would change nothing.
 */
export function useRecentLocations(
  currentPostcode: string,
): UseRecentLocationsResult {
  const { data: meData } = useMe()
  const { tenantSlug } = useParams<{ tenantSlug: string }>()

  const storageKey = buildRecentLocationsKey(tenantSlug, meData?.id)

  const rawStored = useSyncExternalStore(
    subscribeToRecentLocations,
    () => readRecentLocationsRaw(storageKey),
    () => null,
  )

  const recentPostcodes = useMemo(() => {
    const stored = parseRecentLocations(rawStored)
    const current = normalizePostcode(currentPostcode.trim())
    // One more is stored than is shown, so the cap is applied after hiding
    // the postcode in use rather than before it.
    const offered = current
      ? stored.filter((recent) => normalizePostcode(recent) !== current)
      : stored
    return offered.slice(0, MAX_RECENT_LOCATIONS)
  }, [rawStored, currentPostcode])

  const rememberLocation = useCallback(
    (postcode: string) => rememberRecentLocation(storageKey, postcode),
    [storageKey],
  )

  return { recentPostcodes, rememberLocation }
}
